import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { MapPin, X, PiggyBank, Home, TrendingUp } from "lucide-react";
import EconomicPlanEditor from "@/components/piano/EconomicPlanEditor";

const fmtEuro = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const fmtNum  = (n, d = 0) => new Intl.NumberFormat("it-IT", { maximumFractionDigits: d, minimumFractionDigits: d }).format(n || 0);
const fmtPct  = (n) => `${(Number(n) || 0).toFixed(1)}%`;

const PianoEconomico = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorFor, setEditorFor] = useState(null);
  const canUse = !!user?.capabilities?.piano_economico || user?.role === "admin";

  const load = useCallback(async () => {
    try {
      const r = await api.get("/piano/plans");
      setPlans(r.data || []);
    } catch (e) {
      if (e.response?.status !== 403) console.warn("[piano] load failed:", e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (canUse) load(); else setLoading(false); }, [canUse, load]);

  const removePlan = async (pid) => {
    if (!window.confirm("Rimuovere questo immobile dal Piano Economico?")) return;
    try {
      await api.delete(`/piano/plans/${pid}`);
      setPlans(prev => prev.filter(p => p.property_id !== pid));
      toast.success("Rimosso dal Piano");
    } catch { toast.error("Errore rimozione"); }
  };

  if (!canUse) {
    return (
      <div className="min-h-screen bg-slate-50">
        <AppHeader />
        <main className="max-w-3xl mx-auto px-6 py-16 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg">
            <PiggyBank className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Piano Economico</h1>
          <p className="text-slate-600 mt-3 max-w-md mx-auto">Il tuo piano attuale non include il Piano Economico. Consente di stimare i costi di ristrutturazione, ricavi, ROI e ROE con 3 scenari (peggiore / medio / migliore).</p>
          <Button onClick={() => navigate("/dashboard")} className="mt-6 rounded-full bg-[#0A84FF]" data-testid="piano-back-dashboard">Torna alla Dashboard</Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold">Piano Economico</div>
            <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter mt-1 text-slate-900 flex items-center gap-3">
              <img
                src="/board-icons/calculator.png"
                alt=""
                aria-hidden="true"
                className="w-12 h-12 md:w-14 md:h-14 object-contain select-none"
                style={{ filter: "drop-shadow(0 4px 10px rgba(245,158,11,0.35))" }}
              />
              Piano Economico
            </h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
              Analisi economica multi-scenario di ogni immobile: <b>proposta d'acquisto</b>, <b>manodopera</b>, <b>ricavi</b>, tasse di registro
              dinamiche (1°casa / 2°casa / SRL), imprevisti, ROI, ROE, imposta sostitutiva e <b>materiali</b> con Quantità × €/unità che si riportano in automatico sui scenari.
              <br/>Aggiungi altri immobili dal pulsante <b>Piano</b> sulla riga di ciascun immobile nelle tue schede.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="text-slate-500 text-sm">Caricamento…</div>
        ) : plans.length === 0 ? (
          <div className="ios-card p-12 text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#E5F0FF] flex items-center justify-center mb-4">
              <Home className="w-6 h-6 text-[#12466F]" />
            </div>
            <div className="font-display font-bold text-xl">Piano vuoto</div>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">Vai alle tue schede e clicca l'icona <b>Piano</b> su un immobile per aggiungerlo qui.</p>
            <Button onClick={() => navigate("/boards")} className="mt-5 rounded-full bg-[#0A84FF]" data-testid="piano-goto-boards">Vai alle Schede</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="piano-plans-grid">
            {plans.map(plan => {
              const p = plan.property || {};
              const d = plan.derived || null;
              const utileMedio = d?.utile_lordo?.medio ?? 0;
              const roiMedio = d?.roi?.medio ?? 0;
              const totMedio = d?.tot_spese?.medio ?? plan.total_cost;
              const paracadute = d?.prezzo_paracadute ?? 0;
              return (
                <div key={plan.property_id} className="ios-card overflow-hidden group" data-testid={`piano-plan-${plan.property_id}`}>
                  <button className="block w-full text-left" onClick={() => setEditorFor(plan)} data-testid={`piano-plan-open-${plan.property_id}`}>
                    <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
                      {p.thumb ? (
                        <img src={p.thumb} alt="" className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-300"><Home className="w-8 h-8" /></div>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {p.citta || "—"}{p.macro_area ? ` · ${p.macro_area}` : ""}
                      </div>
                      <div className="font-display font-bold text-base leading-tight mt-1 truncate">{p.indirizzo || "Indirizzo n/d"} {p.civico || ""}</div>
                      <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                        <div><div className="text-[9px] uppercase text-slate-500">Prezzo</div><div className="font-mono-num font-bold text-slate-800 text-sm">{fmtEuro(p.prezzo)}</div></div>
                        <div><div className="text-[9px] uppercase text-slate-500">m²</div><div className="font-mono-num font-bold text-slate-800 text-sm">{fmtNum(p.mq)}</div></div>
                        <div><div className="text-[9px] uppercase text-slate-500">€/m²</div><div className="font-mono-num font-bold text-slate-800 text-sm">{fmtEuro(p.eur_mq)}</div></div>
                      </div>

                      {d ? (
                        <div className="mt-4 pt-3 border-t border-slate-200 space-y-1.5">
                          <div className="flex items-center justify-between text-[11px]">
                            <span className="text-slate-500">Tot. spese + acquisto (medio)</span>
                            <span className="font-mono-num font-bold text-slate-900">{fmtEuro(totMedio)}</span>
                          </div>
                          <div className="flex items-center justify-between text-[11px]">
                            <span className="text-slate-500">Utile lordo (medio)</span>
                            <span className={`font-mono-num font-bold ${utileMedio >= 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid={`piano-card-utile-${plan.property_id}`}>{fmtEuro(utileMedio)}</span>
                          </div>
                          <div className="flex items-center justify-between text-[11px]">
                            <span className="text-slate-500">ROI (medio)</span>
                            <span className={`font-mono-num font-bold ${roiMedio >= 20 ? "text-emerald-700" : roiMedio >= 10 ? "text-amber-700" : "text-rose-700"}`}>{fmtPct(roiMedio)}</span>
                          </div>
                          {paracadute > 0 && (
                            <div className="flex items-center justify-between text-[11px] pt-1.5 border-t border-slate-100">
                              <span className="text-slate-500 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Tot. materiali</span>
                              <span className="font-mono-num text-slate-700">{fmtEuro(d?.materiali_totale ?? 0)}</span>
                            </div>
                          )}
                          <div className="text-[10px] text-slate-400 mt-2">Click per aprire il piano completo</div>
                        </div>
                      ) : (
                        <div className="mt-4 pt-3 border-t border-slate-200">
                          <div className="text-[11px] text-slate-500">Costo ristrutt.: <b className="text-slate-800 font-mono-num">{fmtEuro(plan.total_cost)}</b></div>
                          <div className="text-[10px] text-slate-400 mt-2">Click per aprire il piano completo</div>
                        </div>
                      )}
                    </div>
                  </button>
                  <div className="px-4 pb-3 flex justify-end">
                    <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50 h-7 rounded-full text-[11px]" onClick={() => removePlan(plan.property_id)} data-testid={`piano-remove-${plan.property_id}`}>
                      <X className="w-3 h-3 mr-1" /> Rimuovi
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <EconomicPlanEditor
        open={!!editorFor}
        onOpenChange={(v) => !v && setEditorFor(null)}
        plan={editorFor}
        onSaved={() => load()}
      />
    </div>
  );
};

export default PianoEconomico;
